import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CheckIcon,
  Heading,
  Image,
  List,
  Text,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import styles from './CommentReplyFormHeader.module.css'

type CtaOptions = {
  location: string
  button: string
}

type SuggestionsPaywallProps = {
  ctaProperties: CtaOptions
  children: React.ReactNode
}

export function SuggestionsPaywall({
  children,
  ctaProperties,
}: SuggestionsPaywallProps): JSX.Element {
  const currentOrganizationId = useOrganizationId()
  const openGlobalModal = useOpenGlobalModal()
  const [open, setOpen] = useState(false)

  const view = 'community'

  const { location, button } = ctaProperties
  const cta = `publish-${view}-${location}-${button}-1` as const

  const upgradePathName = 'community-suggestions-paywall'

  const SUGGESTIONS_PAYWALL_DATA = {
    heading: 'Upgrade to generate more reply suggestions',
    body: 'You’ve reached the free plan limit of 5 reply suggestions this week. Unlock unlimited suggestions and so much more by upgrading your plan.',
    bulletPoints: [
      'Unlimited reply suggestions',
      'More saved replies for your comments',
      'Schedule up to 2000 posts at a time',
      'Advanced publishing and planning features',
      'In-depth social analytics and reposting',
    ],
    ctaText: 'See Upgrade Options',
  }

  const onCTAClick = (): void => {
    BufferTracker.cTAClicked({
      clientName: Client.PublishWeb,
      product: 'publish',
      cta,
      upgradePathName,
      organizationId: currentOrganizationId ?? '',
    })

    setOpen(false)
    openGlobalModal(MODALS.planSelector, {
      cta,
      upgradePathName,
      planSelector: {
        initialStep: 'upgrade',
      },
    })
  }

  const handleOpen = (isOpen: boolean): void => {
    if (isOpen) {
      BufferTracker.upgradePathViewed({
        clientName: Client.PublishWeb,
        product: 'publish',
        organizationId: currentOrganizationId ?? '',
        upgradePathName,
        cta,
      })
    }
    setOpen(isOpen)
  }

  return (
    <UpgradeDialog open={open} onOpenChange={handleOpen}>
      <UpgradeDialog.Trigger>{children}</UpgradeDialog.Trigger>
      <UpgradeDialog.Content size="xlarge">
        <UpgradeDialog.TextContent>
          <Heading size="lg">{SUGGESTIONS_PAYWALL_DATA.heading}</Heading>
          <Text>{SUGGESTIONS_PAYWALL_DATA.body}</Text>
          <List className={styles.paywallList}>
            {SUGGESTIONS_PAYWALL_DATA.bulletPoints.map((item) => {
              return (
                <List.Item key={item} icon={CheckIcon}>
                  {item}
                </List.Item>
              )
            })}
          </List>
          <Button variant="primary" size="large" onClick={onCTAClick}>
            {SUGGESTIONS_PAYWALL_DATA.ctaText}
          </Button>
        </UpgradeDialog.TextContent>
        <UpgradeDialog.Image>
          <Image
            src="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            alt="Upgrade to have unlimited reply suggestions"
            placeholder="https://buffer-publish.s3.amazonaws.com/images/Upgrade-Comments-Suggestions/upgrade-comments-suggestions.png"
            className={styles.paywallImage}
          />
        </UpgradeDialog.Image>
      </UpgradeDialog.Content>
    </UpgradeDialog>
  )
}
